/**
 * @file backState.ts
 * @brief "Where did I come from": the router state a page's Back returns to.
 *
 * @details
 * DES 13-10-2026: a to-do opened its meeting, and the meeting's Back went to
 * the meetings list, not back to the to-do. The page that opens another one
 * passes `{ backTo, backLabel }` as router state (`backStateHere`); the opened
 * page's Back goes to `backTo` when there is one (`readBackState`), and to its
 * own list otherwise (a bookmark, a new tab, a reload of an old entry).
 * NoteDetailPage already read this shape; meeting detail now does too.
 *
 * Only an in-app path is honoured ("/…", never "//…" or a URL), so the state
 * can never send Back off the site.
 *
 * @author DES
 * @date 13-10-2026
 */

export type BackState = { backTo: string; backLabel?: string };

/** The Back target in a location's state, when it is a safe in-app path. */
export function readBackState(state: unknown): BackState | null {
  if (!state || typeof state !== "object") return null;
  const { backTo, backLabel } = state as { backTo?: unknown; backLabel?: unknown };
  if (typeof backTo !== "string" || !backTo.startsWith("/") || backTo.startsWith("//")) return null;
  return { backTo, backLabel: typeof backLabel === "string" && backLabel ? backLabel : undefined };
}

/** State that brings Back to this page (path + query, so tabs and search survive). */
export function backStateHere(
  location: { pathname: string; search: string },
  backLabel: string,
): BackState {
  return { backTo: `${location.pathname}${location.search}`, backLabel };
}
